import { Injectable } from '@angular/core';
import { CacheService } from 'projects/common/src/app/services';
import { ISession } from 'projects/common/src/app/interfaces/authentication';
import { Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { IMineZone } from '../../interfaces/mine-zone';
import { capitalizeText } from '../../../../../../projects/common/src/app/services/utils/util.service';
import { CommunicationService } from '../../../../services/communication/communication.service';
import { ParameterHandlerService } from '../../../../../../projects/common/src/app/services/parameter-handler.service';
import { IUser } from '../../../../../../projects/common/src/app/interfaces/user';
import { IOperator } from 'src/app/modules/widget/interfaces/operator';
import { IMachine } from 'src/app/modules/administration/interfaces/machine';
import { UrlBuilderService } from 'projects/communication/src/app/services/url-builder.service';

@Injectable({
  providedIn: 'root',
})
export class FilterHandlerService {
  private filterBtnState = new Subject<boolean>();
  public filterBtnState$ = this.filterBtnState.asObservable();

  public clearFilter$: Subject<void>;

  private baseUrl: string;

  constructor(
    private cacheService: CacheService,
    private communicationService: CommunicationService,
    private parameterService: ParameterHandlerService,
    private urlBuilderService: UrlBuilderService,
  ) {
    this.clearFilter$ = new Subject<void>();
    this.baseUrl = this.urlBuilderService.serverBasePath;
  }

  /**
   * Function to get the shifts by company
   */
  public getShiftByCompany(): Promise<any> {
    const session = this.cacheService.local('session') as ISession;
    const companyId = session.user.company.id;
    const url = `${this.baseUrl}/Report/GetShiftByCompany?companyId=${companyId}`;

    return this.communicationService.get(url).toPromise();
  }

  /**
   * Function to get all machines
   */
  public getAllMachines(): Promise<any> {
    const session = this.cacheService.local('session') as ISession;
    const companyId = session.user.company.id;
    const url = `${this.baseUrl}/Machine/GetMachineByCompany?companyId=${companyId}`;

    return this.communicationService.get(url).toPromise();
  }

  /**
   * Function to get all machines from recieved machinetype
   */
  public getMachineByMachineType(machineTypeId: any): Promise<any> {
    const session = this.cacheService.local('session') as ISession;
    const companyId = session.user.company.id;
    const url = `${this.baseUrl}/Report/GetMachinesByMachineType?companyId=${companyId}&machineTypeList=${machineTypeId}`;
    return this.communicationService.get(url).toPromise();
  }

  /**
   * Function to get all mines for selector
   */
  public getAvailableMines(machineTypeId: number): Promise<IMineZone[]> {
    const user = this.parameterService.parameters.item('user-data').value as IUser;
    const params = { machineTypeId, companyId: user.company.id };

    const url = `${this.baseUrl}/Zone/GetMineZoneByMachineType`;
    return this.communicationService.get(url, params).toPromise();
  }

  /**
   * Function to get zone and fronts.
   */
  public getFronts(): Promise<any> {
    const url = `${this.baseUrl}/front`;
    return this.communicationService.get(url).toPromise();
  }

  /**
   * Function to get operator by selected mine
   *
   * @param mineId Mine ID to search operators
   */
  public getOperatorsBySelectedMine(mineId: number, machineTypeId: number): Promise<IOperator[]> {
    const params = {
      mineId: String(mineId),
      machineTypeId,
    };

    const url = `${this.baseUrl}/Operator/GetOperatorByMine`;
    return this.communicationService
      .get(url, params)
      .pipe(
        map((data) => {
          const operatorList = data ? this.operatorMapper(data) : [];
          return operatorList;
        }),
      )
      .toPromise();
  }

  /**
   * Function to get operator by selected mine
   *
   * @param mineId Mine ID to search operators
   */
  public getMachinesBySelectedMine(mineId: number, machineTypeId: number): Promise<IMachine[]> {
    const companyId = this.cacheService.local('session').user.company.id;
    const params = {
      mineId: mineId.toString(),
      companyId,
      machineTypeId,
    };

    const url = `${this.baseUrl}/Machine/GetMachineByMineAndCompany`;
    return this.communicationService
      .get(url, params)
      .pipe(
        map((data) => {
          const machineList = data || [];
          return machineList;
        }),
      )
      .toPromise();
  }

  /**
   * Function to get machines for vital signs filter selector
   */
  public getMachinesForVitalSignsFilter(machineType: any): Promise<any[]> {
    const user = this.parameterService.parameters.item('user-data').value as IUser;
    const params = { companyId: user.company.id, machineType };

    const url = `${this.baseUrl}/Report/GetMachinesForVitalSignsFilter`;
    return this.communicationService.get(url, params).toPromise();
  }

  /**
   * Function to map operator list to capitalize, get fullName & order by name
   *
   * @param data Operator list to map
   */
  private operatorMapper(data: IOperator[]): IOperator[] {
    const operatorList = [];

    if (data) {
      for (const operator of data) {
        let operatorMapped: IOperator = {};
        operatorMapped = {
          groupCategory: 'Todos',
          fullName: capitalizeText(`${operator.firstName.toLowerCase()} ${operator.lastName.toLowerCase()}`),
          ...operator,
        } as IOperator;
        operatorList.push(operatorMapped);
      }
    }
    return operatorList.sort((current, next) => current.firstName.localeCompare(next.firstName));
  }
  /**
   * Function to get all mine.
   */
  public getAllMine(): Promise<any> {
    const url = `${this.baseUrl}/Zone`;
    return this.communicationService.get(url).toPromise();
  }
  /**
   * Emit the state to the component to enable o disabled filter button
   *
   * @param value value to handle the state of de filter button
   */
  public disabledFilterButton(value: boolean): void {
    this.filterBtnState.next(value);
  }
}
